import type { ClientLocationSummaryData } from "api-types/locations/locationsResponses";
import { getBuilderLocationSummaryURL, getBuilderLocationsSummaryURL } from "lib/endpointHelpers";
import { fetcher, useFetch } from "lib/fetcher";
import { useCallback, useState } from "react";
import type { LocationSummaryScope } from "./locationSummaryTypes";

function getLocationSummaryURL(scope: LocationSummaryScope, refresh?: boolean): string {
  if (scope.kind === "builder") {
    return getBuilderLocationsSummaryURL({ builderId: scope.builderId, refresh });
  }

  return getBuilderLocationSummaryURL({
    builderId: scope.builderId,
    locationId: scope.locationId,
    includeChildren: scope.filters.includeChildren,
    refresh,
  });
}

export function useLocationSummary(scope: LocationSummaryScope): {
  data: ClientLocationSummaryData | undefined;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  isRefreshing: boolean;
} {
  const summaryUrl = getLocationSummaryURL(scope);
  const refreshUrl = getLocationSummaryURL(scope, true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data, loading, error, mutate } = useFetch<ClientLocationSummaryData>(
    summaryUrl,
    [
      scope.builderId,
      scope.kind === "location" ? scope.locationId : "builder",
      scope.kind === "location" && scope.filters.includeChildren === false ? "exclude-children" : "include-children",
    ],
    {
      handleErrorLocally: true,
    },
  );

  const refresh = useCallback(async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      const freshData = await fetcher<ClientLocationSummaryData>(refreshUrl);
      await mutate(freshData, { revalidate: false });
    } finally {
      setIsRefreshing(false);
    }
  }, [mutate, refreshUrl]);

  return { data, loading, error, refresh, isRefreshing };
}
